import { useEffect, useRef, useCallback } from "react";
import { Loader2 } from "lucide-react";

export default function InfiniteScrollPagination({
    hasMore,
    loading,
    onLoadMore,
    loadingText = "Loading....",
    endText = "You Reached at End",
    hasMoreText = "Scroll for more posts",
    threshold = 100,
    className = "",
    showEndText = true,
}) {
    const observerRef = useRef();
    const loadingRef = useRef();

    const handleObserver = useCallback(
        (entries) => {
            const [target] = entries;
            if (target.isIntersecting && hasMore && !loading) {
                onLoadMore();
            }
        },
        [hasMore, loading, onLoadMore]
    );

    useEffect(() => {
        const element = loadingRef.current;
        const option = {
            root: null,
            rootMargin: `${threshold}px`,
            threshold: 0,
        };

        observerRef.current = new IntersectionObserver(handleObserver, option);
        if (element) observerRef.current.observe(element);

        return () => {
            if (observerRef.current && element) {
                observerRef.current.unobserve(element);
            }
        };
    }, [handleObserver, threshold]);

    return (
        <div className={`w-full py-4 ${className}`}>
            {(hasMore || loading) && (
                <div ref={loadingRef} className="flex items-center justify-center py-6">
                    {loading ? (
                        <div className="flex items-center gap-2 text-gray-600">
                            <Loader2 className="w-5 h-5 animate-spin" />
                            <span className="text-sm font-medium">{loadingText}</span>
                        </div>
                    ) : hasMore ? (
                        <div className="text-center">
                            <div className="w-full h-px bg-gray-200 mb-4" />
                            <span className="text-sm text-gray-500">
                                {hasMoreText}
                            </span>
                        </div>
                    ) : null}
                </div>
            )}

            {!hasMore && !loading && showEndText && (
                <div className="text-center py-6">
                    <div className="w-full h-px bg-gray-200 mb-4" />
                    <span className="text-sm text-gray-500">{endText}</span>
                </div>
            )}
        </div>
    );
}